/**
 * One canonical spelling for a product's option query string.
 *
 * The same variant used to be reachable at three different URLs, because
 * three places built the link three ways:
 *
 *   product cards      ?Colour=BLACK%2FFLUO%20YELLOW&Size=XXL
 *   size picker        ?Size=XXL&Colour=BLACK%2FFLUO+YELLOW
 *   colour picker      ?Colour=BLACK%2FFLUO+YELLOW&Size=XXL
 *
 * The cards hand-built the string with `encodeURIComponent` (space → '%20')
 * and always put the colour first; the pickers used
 * `URLSearchParams.toString()` (space → '+') and kept whatever key order the
 * URL they were rendered from happened to carry, so arriving from a card
 * flipped every subsequent link into the other order.
 *
 * Those are one page to a shopper but three URLs to Google: each is crawled
 * separately and then discarded as an alternate of the bare product URL, so
 * every variant spent three times the crawl budget it needed. Search Console
 * counts the discards, which is how a few thousand products turn into
 * hundreds of thousands of "alternate page with proper canonical tag" rows.
 *
 * One key order, one encoder, one URL per variant.
 */

/**
 * Colour before size, matching the order a shopper picks them in; anything
 * else sorts after, alphabetically. Products carry either 'Colour' or
 * 'Color', never both.
 */
const OPTION_ORDER = ['Colour', 'Color', 'Size'];

function rank(name: string): number {
  const i = OPTION_ORDER.indexOf(name);
  return i === -1 ? OPTION_ORDER.length : i;
}

export type ProductOptions = Record<string, string | null | undefined>;

/**
 * Read the option params already on a URL so a link can carry them forward,
 * keeping only the names this product actually has.
 *
 * Carrying every param forward turned one tagged visit into a whole crawlable
 * tree: land on a product with ?fbclid=ABC and every size and colour link
 * inherits it, so that one tag is multiplied by the product's entire variant
 * set. Tracking params arrive from anywhere and never repeat, so the space
 * they open has no bound. The canonical drops them, but Google still has to
 * crawl each one to find that out.
 *
 * Names are matched case-insensitively and rewritten to the product's own
 * spelling, so an arriving '?colour=BLACK' also folds onto '?Colour=BLACK'
 * instead of becoming a second URL for the same variant.
 */
export function optionsFromSearch(
  search: string,
  optionNames: (string | undefined)[],
): ProductOptions {
  const known = new Map<string, string>();
  for (const name of optionNames) {
    if (name) known.set(name.toLowerCase(), name);
  }

  const options: ProductOptions = {};
  new URLSearchParams(search).forEach((value, name) => {
    const canonicalName = known.get(name.toLowerCase());
    if (canonicalName) options[canonicalName] = value;
  });
  return options;
}

/** Serialise options into a query string, or '' when none are set. */
export function productOptionQuery(options: ProductOptions): string {
  const entries = Object.entries(options)
    .map(([name, value]) => [name.trim(), (value ?? '').trim()] as const)
    .filter(([name, value]) => name !== '' && value !== '')
    .sort(
      ([a], [b]) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0),
    );

  return entries
    .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
    .join('&');
}

/** A product path with its options in the one canonical spelling. */
export function productVariantPath(
  pathname: string,
  options: ProductOptions,
): string {
  const query = productOptionQuery(options);
  return query ? `${pathname}?${query}` : pathname;
}
